import { AutoComplete } from "antd";
import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { searchable } from "utils/helpers/text";

const PayloadPathsContext = createContext([]);

export const PayloadPathsProvider = ({ paths, children }) => {
  const value = useMemo(() => paths || [], [paths]);
  return (
    <PayloadPathsContext.Provider value={value}>
      {children}
    </PayloadPathsContext.Provider>
  );
};

export const usePayloadPaths = () => useContext(PayloadPathsContext);

export const toPayloadPathOptions = (schemaPaths = []) =>
  schemaPaths.map(({ path, type, sample }) => ({
    value: path,
    label: path,
    type,
    sample,
  }));

const matches = (option, query) => {
  const q = searchable(query);
  if (!q) return true;
  return [option.value, option.label, option.sample].some((text) =>
    searchable(text).includes(q),
  );
};

const PayloadPathSelect = ({
  value,
  onChange,
  placeholder,
  size = "small",
  className = "dynamic-documents__field-full",
  allowClear = true,
}) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const PAYLOAD_PATHS = t("PAYLOAD_PATHS", { returnObjects: true });
  const paths = usePayloadPaths();

  const options = useMemo(
    () =>
      paths.map((option) => ({
        value: option.value,
        label: (
          <div>
            <div className="dynamic-documents__payload-option-label">
              {option.label || option.value}
            </div>
            <div className="dynamic-documents__payload-option-path">
              {option.sample
                ? `${option.value} · ${option.sample}`
                : option.value}
            </div>
          </div>
        ),
      })),
    [paths],
  );

  return (
    <AutoComplete
      allowClear={allowClear}
      size={size}
      className={className}
      placeholder={placeholder ?? PAYLOAD_PATHS.PLACEHOLDER}
      value={value || undefined}
      onChange={(next) => onChange(next || "")}
      filterOption={(input, option) =>
        matches(
          paths.find((p) => p.value === option.value) || {
            value: option.value,
          },
          input,
        )
      }
      options={options}
    />
  );
};

export default PayloadPathSelect;
